import { useEffect, useRef, type ReactNode } from 'react';
import { Wrench } from 'lucide-react';
import { maintenanceStore, useMaintenance } from './store';

export function MaintenanceScreen() {
  const heading = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    heading.current?.focus({ preventScroll: true });
    let checking = false;
    let cancelled = false;
    let controller: AbortController | undefined;
    const timer = window.setInterval(async () => {
      if (checking) return;
      checking = true;
      controller = new AbortController();
      const timeout = window.setTimeout(() => controller?.abort(), 30000);
      try {
        // Unique URL prevents the service worker returning an old cached 200.
        // This public health probe never redirects a session or replays a write.
        const response = await fetch(`${import.meta.env.VITE_API_URL || ''}/api/v1/health?maintenance_check=${Date.now()}`, {
          cache: 'no-store', credentials: 'include', headers: { Accept: 'application/json' }, signal: controller.signal,
        });
        if (response.ok && !cancelled) maintenanceStore.set(false);
      } catch { /* Offline or still unavailable: keep the explanation and saved entries. */ }
      finally { window.clearTimeout(timeout); checking = false; }
    }, 60000);
    return () => { cancelled = true; controller?.abort(); window.clearInterval(timer); };
  }, []);

  return (
    <section className="mx-auto w-full min-w-0 max-w-2xl break-words px-4 py-12 sm:px-6" aria-labelledby="maintenance-heading">
      <div className="rounded-lg border border-amber-500 bg-graphite-900 p-6">
        <div className="mb-4 flex items-center gap-2 text-sm font-semibold text-amber-300"><Wrench size={20} aria-hidden="true" />Scheduled database upgrade</div>
        <h1 ref={heading} id="maintenance-heading" tabIndex={-1} className="text-2xl font-semibold text-graphite-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-amber-500">DemoOps is down for a database upgrade</h1>
        <p className="mt-4 text-base leading-relaxed text-graphite-200">Your entries are safe; anything saved on this device will send automatically when we are back.</p>
        <p className="mt-4 text-sm text-graphite-300">We will be back as soon as the upgrade is finished. Keep this tab open.</p>
        <p role="status" className="mt-4 text-sm text-amber-300">Checking every 60 seconds. The app will return automatically when the upgrade is done.</p>
      </div>
    </section>
  );
}

/** Hide rather than unmount: preserve unsaved form state during a maintenance response. */
export function MaintenanceBoundary({ children }: { children: ReactNode }) {
  const active = useMaintenance();
  return <>{active && <MaintenanceScreen />}<div hidden={active}>{children}</div></>;
}
